import React, { useEffect, useState } from "react";
import { apiRequest } from "../api";
import countries from "../data/countries.json"; // Import the API utility

const AddTourModal = ({ tour, onClose, onSave, universities, isMultiUser }) => {
    const [name, setName] = useState("");
    const [country, setCountry] = useState("");
    const [date_from, setDateFrom] = useState("");
    const [date_to, setDateTo] = useState("");
    const [selectedUniversity, setSelectedUniversity] = useState("");
    const [selectedUniversities, setSelectedUniversities] = useState([]); // For multi-select
    const [feedbackMessage, setFeedbackMessage] = useState(null);
    const [isLoading, setIsLoading] = useState(false); // Loading state
    const apiUrl = process.env.REACT_APP_API_BASE_URL;
    const token = localStorage.getItem("token"); // Get token from localStorage
    const universityId = localStorage.getItem("universityId");

    useEffect(() => {
        if (tour) {
            setName(tour.name);
            setCountry(tour.country || "");
            setDateFrom(tour.date_from);
            setDateTo(tour.date_to);
            if (isMultiUser && tour.university?.id) {
                setSelectedUniversity(tour.university.id);
            }
        } else {
            setName("");
            setCountry("");
            setDateFrom("");
            setDateTo("");
            setSelectedUniversity("");
            setSelectedUniversities([]);
        }
    }, [tour, isMultiUser]);

    const formatDate = (date) => {
        const [year, month, day] = date.split("-");
        return `${month}/${day}/${year}`;
    };

    const handleUniversityChange = (universityId) => {
        setSelectedUniversities(prev => {
            if (prev.includes(universityId)) {
                return prev.filter(id => id !== universityId);
            } else {
                return [...prev, universityId];
            }
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // For multi-users, require university selection
        if (isMultiUser && selectedUniversities.length === 0) {
            setFeedbackMessage("Please select at least one university.");
            return;
        }

        // For regular users, check if universityId is available in localStorage
        if (!isMultiUser && !universityId) {
            setFeedbackMessage("University ID not found. Please log in again.");
            return;
        }

        setIsLoading(true); // Set loading state
        
        try {
            let data;
            
            if (isMultiUser && !tour) {
                // Multi-tour creation for new tours
                const payload = {
                    name,
                    university_ids: selectedUniversities,
                    country: country || null,
                    date_from: new Date(date_from).toISOString().split("T")[0],
                    date_to: new Date(date_to).toISOString().split("T")[0],
                };
                
                data = await apiRequest(`${apiUrl}/multi-tours`, "POST", payload, token);
            } else {
                // Single tour creation/update
                const payload = {
                    name,
                    country: country || null,
                    date_from: new Date(date_from).toISOString().split("T")[0],
                    date_to: new Date(date_to).toISOString().split("T")[0],
                };
                
                const url = tour
                    ? `${apiUrl}/tours/${tour.id}` // Edit mode
                    : `${apiUrl}/tours/${isMultiUser ? selectedUniversity : universityId}`; // Add mode
                const method = tour ? "PUT" : "POST";

                data = await apiRequest(url, method, payload, token);
            }
            
            console.log(`Tour ${tour ? "updated" : "added"} successfully`, data);
            setFeedbackMessage(`Tour ${tour ? "updated" : "added"} successfully!`);
            setTimeout(() => {
                setFeedbackMessage(null);
                setIsLoading(false); // Reset loading state
                onSave(); // Notify parent to refresh list
                onClose(); // Close the modal
            }, 2000);
        } catch (error) {
            setIsLoading(false); // Reset loading state
            setFeedbackMessage(`Failed to ${tour ? "update" : "add"} tour. Please try again.`);
        }
    };

    return (
        <div
            style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 1000,
            }}
        >
            <div
                style={{
                    backgroundColor: "#fff",
                    padding: "20px",
                    borderRadius: "5px",
                    width: "500px",
                    maxHeight: "90vh",
                    overflowY: "auto",
                }}
            >
                <h4>{tour ? "Edit Tour" : "Add New Tour"}</h4>
                <form onSubmit={handleSubmit}>
                    {isMultiUser && (
                        <div className="form-group">
                            <label>Universities:</label>
                            {!tour ? (
                                // Multi-select for new tours
                                <div style={{ maxHeight: "200px", overflowY: "auto", border: "1px solid #ddd", borderRadius: "4px", padding: "10px" }}>
                                    {universities?.map((university) => (
                                        <div key={university.id} style={{ marginBottom: "8px" }}>
                                            <label style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedUniversities.includes(university.id)}
                                                    onChange={() => handleUniversityChange(university.id)}
                                                    disabled={isLoading}
                                                    style={{ marginRight: "8px" }}
                                                />
                                                {university.name}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                // Single select for editing existing tours
                                <select
                                    className="form-control"
                                    value={selectedUniversity}
                                    onChange={(e) => setSelectedUniversity(e.target.value)}
                                    required
                                    disabled={isLoading}
                                >
                                    <option value="">-- Select a University --</option>
                                    {universities?.map((university) => (
                                        <option key={university.id} value={university.id}>
                                            {university.name}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>
                    )}
                    <div className="form-group">
                        <label>Tour Name:</label>
                        <input
                            type="text"
                            className="form-control"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                            disabled={isLoading}
                        />
                    </div>
                    <div className="form-group">
                        <label>Date From:</label>
                        <input
                            type="date"
                            className="form-control"
                            value={date_from}
                            onChange={(e) => setDateFrom(e.target.value)}
                            required
                            disabled={isLoading}
                        />
                    </div>
                    <div className="form-group">
                        <label>Date To:</label>
                        <input
                            type="date"
                            className="form-control"
                            value={date_to}
                            onChange={(e) => setDateTo(e.target.value)}
                            required
                            disabled={isLoading}
                        />
                    </div>
                    <div className="form-group">
                        <label>Country (Optional):</label>
                        <select
                            className="form-control"
                            value={country}
                            onChange={(e) => setCountry(e.target.value)}
                            disabled={isLoading}
                        >
                            <option value="" disabled>
                                -- Select a Country --
                            </option>
                            {countries.map((c) => (
                                <option key={c.name} value={c.name}>
                                    {c.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="modal-footer">
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={isLoading}
                        >
                            {isLoading ? "Submitting..." : tour ? "Update" : "Save"}
                        </button>
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={onClose}
                            disabled={isLoading}
                        >
                            Cancel
                        </button>
                    </div>
                </form>
                {feedbackMessage && (
                    <p
                        style={{
                            marginTop: "10px",
                            color:
                                feedbackMessage.includes("successfully")
                                    ? "green"
                                    : "red",
                        }}
                    >
                        {feedbackMessage}
                    </p>
                )}
            </div>
        </div>
    );
};

export default AddTourModal;